import React from "react";
import {Column, Header, Table, flexRender} from "@tanstack/react-table";
import {Workbook} from "exceljs";
import {saveAs} from "file-saver";


function getNodeText(node: React.ReactNode): string {
    if (node === null || node === undefined || typeof node === 'boolean') return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(getNodeText).join('');
    if (React.isValidElement(node)) return getNodeText(node.props?.children);
    return '';
}

function normalizeHeaderLabel(value: unknown, fallback: string): string {
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
    }
    if (Array.isArray(value) || React.isValidElement(value)) {
        const text = getNodeText(value as React.ReactNode).replace(/\s+/g, ' ').trim();
        return text || fallback;
    }
    return fallback;
}

function getColumnHeaderLabel(
    column: Column<any, any>,
    headerContextById: Map<string, Header<any, any>>
): string {
    const headerContext = headerContextById.get(column.id);
    if (headerContext) {
        const rendered = flexRender(
            headerContext.column.columnDef.header,
            headerContext.getContext()
        );
        return normalizeHeaderLabel(rendered, column.id);
    }
    return normalizeHeaderLabel(column.columnDef.header, column.id);
}

// Exports table data
export async function exportTableData(
    table: Table<any>,
    filename: string,
    applyFilters: boolean = true,
    filetype: string = 'csv',
    includeHiddenColumns: boolean = false,
    excludeColumnIds: string[] = []
) {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet()
    const lastHeaderGroup = table.getHeaderGroups().at(-1)
    if (!lastHeaderGroup) {
        console.error('No Header groups found')
        return
    }
    const headerContextById = new Map<string, Header<any, any>>();
    table.getHeaderGroups().forEach((group) => {
        group.headers.forEach((header) => {
            if (!header.isPlaceholder) {
                headerContextById.set(header.column.id, header);
            }
        });
    });

    const columns = includeHiddenColumns
        ? table.getAllLeafColumns()
        : lastHeaderGroup.headers.filter((header) => header.column.getIsVisible()).map((header) => header.column);
    const filteredColumns = columns.filter((column) => !excludeColumnIds.includes(column.id));

    // For each header, get the header def string and set it as one of the header columns
    worksheet.columns = filteredColumns.map((column) => ({
        header: getColumnHeaderLabel(column, headerContextById),
        key: column.id,
        width: 20 // adjust as needed
    }))

    const exportRows = applyFilters ? table.getFilteredRowModel().rows : table.getCoreRowModel().rows

    exportRows.forEach((row) => {
        const values = filteredColumns.map((column) => row.getValue(column.id) ?? '')
        worksheet.addRow(values)
    })
    worksheet.getRow(1).eachCell((cell) => {
        cell.font = {bold: true}
    })
    const buffer = filetype==='csv' ? await workbook.csv.writeBuffer() : await workbook.xlsx.writeBuffer()
    saveAs(new Blob([buffer]), `${filename}.${filetype}`)
}
